import * as z from 'zod';
import { MAX_NOTES_LENGTH } from '../limits.js';
import { idempotencyKeySchema, isoDateTimeSchema, recordActionMethodSchema, recordedRefusalSchema, shortRefSchema, strict, uuidSchema, } from './common.js';
import { credentialTypeSchema } from './credential.js';
import { diningStateSchema } from './dining.js';
import { cursorSchema, DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from './pagination.js';
import { personSummarySchema, presenceStateSchema } from './person.js';
/**
 * The gate: resolving a pass, recording what happened, and reading the movement record back.
 *
 * Section 1.11's outcome matrix is the specification, and the shapes here are its wire form. Four
 * of its rules show up directly.
 *
 * **The pass code travels in a body and never in a URL (AD-28).** There is no schema here for a
 * path or query parameter carrying a code, because there is no such route: a code in a path is
 * written in cleartext into two access logs, browser history and any `Referer`, all outside the
 * encryption boundary, and a code plus a scan is full impersonation.
 *
 * **A rejection does not consume the idempotency key (AD-30).** The key is submitted with the
 * attempt and is consumed only by an outcome that changed state, so the operator's authorised
 * override after a blocked person's refusal is written under the same key the refusal did not spend.
 *
 * **An API key never overrides (AD-33).** The authorization layer is what refuses the override, and
 * it refuses it with its own error code rather than by pretending the field was invalid.
 *
 * **A refused MOVEMENT is not a refused REQUEST, and the two shapes say so.** Every row of the
 * matrix that gets as far as a decision writes a `presence_events` row — including the three that
 * refuse the movement — so {@link submitPresenceResponseSchema} carries the event, the resulting
 * state, and a nullable {@link recordedRefusalSchema} saying why the person did not move. A request
 * that never reached a decision (an unknown person, a missing reason code, a reused key) is the
 * AD-35 error body instead, because nothing was written and there is no event to name.
 *
 * `presenceStateSchema` is deliberately NOT declared here: the state lives on the person row
 * (AD-29), so `person.ts` owns it, and declaring it here made the two modules import each other.
 */
export const directionSchema = z.enum(['entry', 'exit']);
/**
 * The closed list of reasons an operator may give for an override (AD-31).
 *
 * It is a closed list rather than free text because undefined behaviour at a gate becomes
 * improvised behaviour: an override is the moment the system is being told its own record is
 * wrong, and "why" has to be answerable later from the row alone. `other` is the escape hatch and
 * it is the one value that additionally requires a note, which is encrypted under the person's key
 * because an operator writing prose about a specific human is writing personal data.
 *
 * The list lives here rather than only in the database because the column is a PostgreSQL enum: a
 * value the API can produce that the column cannot store is a 500 at a gate, and the schema test
 * compares the two member lists so neither can grow alone.
 */
export const PRESENCE_REASON_CODES = [
    'left_via_other_exit',
    'entered_via_other_entrance',
    'gate_unattended',
    'suspected_pass_sharing',
    'operator_error',
    'system_correction',
    'other',
];
/**
 * The reason codes as a schema, and what a request carrying one is validated against.
 *
 * Narrower than the `reasonCodeSchema` wire primitive the credential and person requests use, and
 * deliberately: the column is a PostgreSQL enum, so a token outside this list is a value the
 * database cannot store, and refusing it here names the offending FIELD rather than answering a
 * generic code from inside the domain. `PRESENCE_REASON_UNKNOWN` stays reachable, because
 * `system_correction` is a member of this list that an operator's picker never offers and the
 * presence service refuses from a human.
 */
export const presenceReasonCodeSchema = z.enum(PRESENCE_REASON_CODES);
export const OPERATOR_SELECTABLE_REASON_CODES = PRESENCE_REASON_CODES.filter((code) => code !== 'system_correction');
/** The one reason that is not self-explanatory, and therefore the one that requires a note. */
export const REASON_CODE_REQUIRING_NOTE = 'other';
/** What was recorded. `rejected` changes no state, which is why it consumes no idempotency key. */
export const presenceResultSchema = z.enum([
    'accepted',
    'override',
    'blocked_override',
    'rejected',
]);
/** How the movement reached the system. The reserved values exist so a later gate needs no enum change. */
export const presenceMethodSchema = z.enum([
    'scan_hardware',
    'scan_camera',
    'scan_upload',
    'manual_key',
    'manual_search',
    'api',
    'system',
    'reconciliation',
    'manual_backfill',
]);
/**
 * Why a scan failed before it ever reached a person. Recorded as security telemetry.
 *
 * The last two are AD-60's: a well-formed code that matches no credential is checked against the
 * imported blanks before it is called `unknown`, because "this band exists and nobody has
 * registered it" and "this band was withdrawn" are different facts at a desk, and reporting
 * either of them as `unknown` sends an operator looking for a fault that is not there.
 */
export const scanAttemptOutcomeSchema = z.enum([
    'unknown',
    'revoked',
    'superseded',
    'malformed',
    'rate_limited',
    'blank_unclaimed',
    'blank_void',
]);
/**
 * What the gate screen must show the operator BEFORE they submit anything.
 *
 * A closed set of tokens rather than sentences, for the reason every reason code is one: the client
 * renders each in the reader's language, and a server-composed sentence would be a second
 * catalogue. Each is derivable without decrypting the record, which is what keeps a scan cheap.
 *
 * `no_photograph` is the one that is easy to mistake for cosmetic. AD-66 states that the operator
 * seeing the holder's stored photograph is the PRIMARY control against a forged pre-printed band —
 * the supplier holds every code in the batch before any of it is issued — so a record with no
 * photograph is a scan where that control does not exist, and saying so is the honest answer.
 */
export const gateWarningSchema = z.enum([
    'person_blocked',
    'person_archived',
    'no_photograph',
    'name_unavailable',
    /**
     * There is an open `outside_too_long` alert for this person: they went out and are late back.
     *
     * Read from the alert rather than recomputed, so the door says exactly what the monitor and the
     * alert list say and cannot drift from them. The price is that it lags by at most one sweep, and
     * the moment it is worth saying is the moment they are standing at the gate.
     */
    'outside_too_long',
]);
/**
 * Resolving a scan. The code is in the body; there is no route that takes it any other way.
 *
 * **It carries no `method` and no `clientLabel`, and their removal is a fix rather than a
 * simplification.** Resolving decides nothing and writes nothing but a `scan_attempts` row, and
 * that table has neither column — so both members validated and were then silently dropped, which
 * is exactly the "the field validates and nothing happens" defect the API phases exist to avoid.
 * How the code was captured is a property of the MOVEMENT and is carried by
 * {@link submitPresenceRequestSchema}, which has somewhere to put it.
 *
 * `type` replaces them because the resolver genuinely takes one: AD-29's reserved NFC types sit
 * behind a disabled flag, and a request naming one is told the hardware is off rather than being
 * resolved to nothing.
 */
export const resolveScanRequestSchema = strict({
    code: z.string().min(1).max(64),
    type: credentialTypeSchema.optional(),
});
/**
 * What the operator is shown after a scan.
 *
 * `expectedDirection` is what the person's current state implies, and `permittedActions` is what
 * this operator may actually do, so the screen never offers a button the server would refuse.
 */
export const resolveScanResponseSchema = strict({
    person: personSummarySchema,
    credentialId: uuidSchema,
    expectedDirection: directionSchema,
    permittedActions: z.array(directionSchema),
    warnings: z.array(gateWarningSchema),
    /**
     * Minted by the server so a retry of the submission is deduplicated (AD-30).
     *
     * **One key per scan, spent by up to two domains.** The movement consumes it in
     * `presence_events` and a meal consumes it in `meal_events`, on two partial unique indexes over
     * two tables that cannot see each other, so a scan can record one of each and a retry of either
     * is still answered as the replay it is. Minting a second key would have been a second wire
     * member for a guarantee the tables already give.
     */
    idempotencyKey: idempotencyKeySchema,
    /**
     * What the counter needs, or `null` when this installation does not control meals.
     *
     * **`null` rather than an empty state, and the distinction is what the browser branches on.** A
     * disabled installation has no service date, no suggestion and no allowance to report, and a
     * zeroed block would render three buttons that every submission refuses. The member is present
     * and nullable rather than optional so that "dining is off here" is something the server states.
     */
    dining: diningStateSchema.nullable(),
});
/**
 * Submitting a movement. One transaction, one row lock, one decision (AD-20).
 *
 * **The method is one of the five an operator at a terminal produces, and there is no
 * `occurredAt`.** The service honours a supplied instant only for `manual_backfill` and
 * `reconciliation`, so accepting one here would have been a field that validates and is then
 * ignored — a terminal could believe it had recorded yesterday's entry. Both of those methods have
 * their own route with their own contract, and on this one a backdated instant is refused as an
 * unknown key before any handler runs. `api` joins this list in the phase that adds the API-key
 * actor kind; until then it is a value no caller of this route can produce.
 */
/**
 * How a submission reached this route: the five an operator produces, plus `api`.
 *
 * **`api` joined this list in Phase 20, which is what the comment below always said would happen.**
 * `presenceMethodSchema` is the wider enum and carries four machine methods; three of them —
 * `system`, `reconciliation` and `manual_backfill` — belong to routes and jobs with their own
 * contracts, and admitting one here would let a terminal file a backdated correction through the
 * gate's own endpoint. `api` is different: it names a movement a machine recorded through THIS
 * route, which is exactly what an API key holding `presence:write` does.
 *
 * **The enum does not decide who may send which.** `presence/service.ts` refuses a method whose
 * actor cannot produce it — a human sending `api` and a key sending `scan_hardware` are both
 * refused — because the enum can say which values exist and only the service knows who is asking.
 * Keeping both is what makes the manual-versus-scan ratio of AD-53 mean something: it counts the
 * five, and `api` is not one of them.
 */
export const submitPresenceMethodSchema = z.enum([
    ...recordActionMethodSchema.options,
    'api',
]);
export const submitPresenceRequestSchema = strict({
    /**
     * Who moved.
     *
     * **Required, and it was missing.** The movement is recorded against a PERSON — that is the row
     * AD-20 locks and the state it decides — and a request that named only a credential could not
     * express the submissions Section 1.6 allows: a manual search, a manual key entry, and a
     * registered person who holds no band at all all carry no credential, so with `credentialId`
     * nullable there would be nothing left in the body identifying anybody. The credential beside it
     * is checked against this id under the same lock (AD-36), so naming somebody else's pass is a
     * typed refusal rather than a permanent misattribution on an append-only row.
     */
    personId: uuidSchema,
    /**
     * The pass that was presented, or `null`.
     *
     * Nullable rather than optional: a manual search, a manual key entry and a person who holds no
     * band at all are all legitimate submissions with no credential (Section 1.6), and requiring the
     * member to be PRESENT is what makes "this movement named no pass" something a client states
     * rather than something it forgets.
     */
    credentialId: uuidSchema.nullable(),
    direction: directionSchema,
    method: submitPresenceMethodSchema,
    idempotencyKey: idempotencyKeySchema,
    /** Required when the direction contradicts the state, and only then. */
    reasonCode: presenceReasonCodeSchema.optional(),
    reasonNote: z.string().trim().max(MAX_NOTES_LENGTH).optional(),
    clientLabel: z.string().max(64).optional(),
});
export const presenceEventSchema = strict({
    id: uuidSchema,
    personId: uuidSchema,
    credentialId: uuidSchema.nullable(),
    direction: directionSchema,
    result: presenceResultSchema,
    reasonCode: presenceReasonCodeSchema.nullable(),
    method: presenceMethodSchema,
    operatorId: uuidSchema.nullable(),
    previousState: presenceStateSchema,
    newState: presenceStateSchema,
    occurredAt: isoDateTimeSchema,
    clientLabel: z.string().nullable(),
});
/**
 * The answer to a submission.
 *
 * `replayed` is what an idempotent retry sets, so the client can say "already registered N seconds
 * ago" instead of showing a second confirmation for one movement.
 *
 * **`refusal` is what makes a `rejected` outcome legible.** Section 1.11's blocked, archived and
 * API-key rows are recorded events that refused a movement, so the response has to carry both: the
 * event id the trail knows them by, and the reason the person is still standing at the door. A
 * client renders a green confirmation only when `refusal` is null.
 */
export const submitPresenceResponseSchema = strict({
    event: presenceEventSchema,
    replayed: z.boolean(),
    /** The person's state after this submission, which is what the confirmation renders. */
    presenceState: presenceStateSchema,
    /** The direction the new state implies, which is what the next scan will expect. */
    expectedDirection: directionSchema,
    /** Whether the submission contradicted the recorded state, which the red alert renders. */
    contradicted: z.boolean(),
    refusal: recordedRefusalSchema.nullable(),
});
/**
 * The whole presence picture as one small body, and the instant it was taken.
 *
 * Counts only — no name, no reference, nothing a screen a terminal sits on unattended could leak.
 * `inside` and `outside` are one grouped read of `persons.presence_state`; `overdue` is how many of
 * the outside set are past `overdueAfterMinutes`, which is reported beside it so a client renders
 * the figure with the threshold it was computed against rather than one it fetched separately.
 */
/**
 * The `occupancy` frame on the realtime stream, which is a SUBSET of the body below.
 *
 * **Two counts and an instant, and the omission is the design.** This frame is published after every
 * movement, so it is on the gate's hot path: the two counts are one grouped index read, while the
 * overdue figure needs a lateral join per person who is out. A terminal learns that somebody became
 * overdue from its next read of `GET /api/v1/presence/occupancy`, or from the alert — neither of
 * which is on the path a queue of people is waiting in.
 *
 * Declared here rather than left implicit because it is a wire contract: a client parses it, and
 * `INTEGRATION.md` documents it.
 */
export const occupancyFrameSchema = strict({
    inside: z.int().nonnegative(),
    outside: z.int().nonnegative(),
    at: isoDateTimeSchema,
});
export const occupancySchema = strict({
    ...occupancyFrameSchema.shape,
    overdue: z.int().nonnegative(),
    /** `presence.outside_too_long_minutes` as it stood when this was computed. */
    overdueAfterMinutes: z.int().positive(),
});
/**
 * One line of a presence list: the evacuation roll-call, and the post-restore reconciliation screen.
 *
 * One shape for both, because a line of either is the same four facts about a person — and the
 * outside monitor extends it rather than restating it, so there is exactly one place this shape is
 * declared. What the two lists differ in is the POPULATION: the roll-call answers "who is inside",
 * for an evacuation, and the reconciliation screen answers "who does the restored archive believe
 * is outside", which is the small set an operator can actually walk over and check.
 *
 * `name` is nullable and the reason is stated rather than defensive: the projection excludes a
 * shredded person and a build that is still running excludes everybody, and dropping such a row
 * would produce a roll-call short by exactly the people nobody can identify. The `shortRef` is
 * always there, and it is quotable.
 *
 * The thumbnail is an ID, never bytes. They are fetched through the image route, which authorises
 * and audits each one (AD-43); embedding them would put a hundred photographs into one response.
 */
export const rollCallEntrySchema = strict({
    personId: uuidSchema,
    shortRef: shortRefSchema,
    name: z.string().nullable(),
    since: isoDateTimeSchema.nullable(),
    imageId: uuidSchema.nullable(),
});
export const rollCallSchema = strict({
    generatedAt: isoDateTimeSchema,
    total: z.int().nonnegative(),
    /** False when somebody on the list could not be named. A printed copy has to say why. */
    complete: z.boolean(),
    entries: z.array(rollCallEntrySchema),
});
/**
 * One line of the outside monitor: a roll-call line, plus whether this person is late back.
 *
 * `overdue` is COMPUTED from `since` and the allowance at the instant the list was built, not read
 * from the alert. The two answer different questions and the difference is deliberate: this says
 * "is this person past the allowance right now", and the alert says "does somebody still need to
 * act on it" — which is why acknowledging one changes the second and must not change the first.
 * `overdue` is false when `since` is null, because nothing is claimed about a state with no
 * movement behind it.
 */
export const outsideEntrySchema = strict({
    ...rollCallEntrySchema.shape,
    overdue: z.boolean(),
});
/**
 * Who is outside right now, oldest departure first.
 *
 * **The order is the whole ergonomics of it**: the person who has been out longest is the first row,
 * so the list reads as a queue of people to worry about rather than as a register. It never pages,
 * for the reason the roll-call does not: in a residence the outside set is small by construction,
 * and a monitor that paged would hide the row it exists to show.
 */
export const outsideListSchema = strict({
    generatedAt: isoDateTimeSchema,
    total: z.int().nonnegative(),
    /** How many of them are past the allowance. The figure the landing screen shows. */
    overdue: z.int().nonnegative(),
    /** The allowance this list was computed against. */
    overdueAfterMinutes: z.int().positive(),
    /** False when somebody on the list could not be named, exactly as on the roll-call. */
    complete: z.boolean(),
    entries: z.array(outsideEntrySchema),
});
/**
 * The movement log, filtered and paged.
 *
 * **It takes no sort direction, and `direction` here is the MOVEMENT's.** A movement log has
 * exactly one natural order — newest first — so the `direction` member every other list route
 * spends on `asc`/`desc` is free to mean what it means everywhere else in this domain. The cursor
 * is still bound to the filter it was issued under, so a client that changes a filter and keeps its
 * cursor is refused rather than quietly answered the wrong question.
 */
export const presenceEventQuerySchema = strict({
    personId: uuidSchema.optional(),
    result: presenceResultSchema.optional(),
    direction: directionSchema.optional(),
    method: presenceMethodSchema.optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: cursorSchema.optional(),
});
/**
 * The backfill screen: a movement that happened, recorded after the fact (AD-44).
 *
 * `occurredAt` is REQUIRED and is the whole point of the screen. It carries no `credentialId`,
 * because a backfill attests a movement rather than a scan and naming a pass would put a credential
 * id on a row nobody presented a credential for; and no `method`, because there is exactly one and
 * the route supplies it.
 */
export const backfillPresenceRequestSchema = strict({
    personId: uuidSchema,
    direction: directionSchema,
    occurredAt: isoDateTimeSchema,
    idempotencyKey: idempotencyKeySchema,
    reasonCode: presenceReasonCodeSchema.optional(),
    reasonNote: z.string().trim().max(MAX_NOTES_LENGTH).optional(),
    clientLabel: z.string().max(64).optional(),
});
/**
 * Emptying the building: an exit for everybody inside, with a reason.
 *
 * The reason is required even though the direction will match for everybody inside, so the state
 * machine would not demand one: AD-31's requirement here is about the ACT rather than about the
 * row, and the reason is what the run's audit detail carries.
 *
 * **There is no run id on the wire.** A retry of a completed sweep writes nothing, because every
 * person it touched is now outside and the correction is skipped under their row lock; minting the
 * run id per request is therefore free, and it stops a caller stitching two different sweeps into
 * one audit row.
 */
export const forceExitAllRequestSchema = strict({
    reasonCode: presenceReasonCodeSchema,
    reasonNote: z.string().trim().max(MAX_NOTES_LENGTH).optional(),
});
/** What one bulk correction did. Every number is a count of rows, not an estimate. */
export const bulkCorrectionReportSchema = strict({
    runId: uuidSchema,
    corrected: z.int().nonnegative(),
    skipped: z.int().nonnegative(),
    refused: z.array(strict({ personId: uuidSchema, code: z.string().min(1).max(64) })),
});
//# sourceMappingURL=presence.js.map